import { CHART_SERIES } from '../../components/charts/palette';
import type { ComparisonAlignment, ComparisonMetric } from '../../utils/comparison/alignStreams';

export type ComparisonMode = 'two-workouts' | 'one-workout-segments';
export type ComparisonViewMode = 'full' | 'segments';

export interface SegmentRange {
  startSec: number;
  endSec: number;
}

export interface WorkoutSummary {
  id: number;
  /** Workout owner — required for coach cross-athlete compare and range API. */
  userId?: number;
  title: string;
  workout_day: string;
  total_time: number | null;
  distance: number | null;
  tss_actual: number | null;
  np?: number | null;
  avgPower?: number | null;
  workout_type?: { name?: string; english_name?: string };
}

export interface ComparisonPrefill {
  workoutAId?: number;
  workoutBId?: number;
  workoutAUserId?: number;
  workoutBUserId?: number;
  segmentA?: SegmentRange;
  segmentB?: SegmentRange;
  mode?: ComparisonMode;
}

export interface ComparisonSession {
  mode: ComparisonMode;
  viewMode: ComparisonViewMode;
  metric: ComparisonMetric;
  alignment: ComparisonAlignment;
  workoutA: WorkoutSummary | null;
  workoutB: WorkoutSummary | null;
  segmentA: SegmentRange | null;
  segmentB: SegmentRange | null;
}

export interface SavedComparisonRecord {
  id: string;
  apiId?: number;
  name: string;
  tags: string[];
  notes: string;
  savedAt: string;
  session: ComparisonSession;
  sparklineA?: number[];
  sparklineB?: number[];
  shareToken?: string | null;
}

export const COMPARISON_COLORS = {
  a: CHART_SERIES.primary,
  b: CHART_SERIES.secondary,
} as const;

export const METRIC_TABS: { id: ComparisonMetric; label: string }[] = [
  { id: 'power', label: 'Power' },
  { id: 'heart_rate', label: 'Heart Rate' },
  { id: 'cadence', label: 'Cadence' },
  { id: 'speed', label: 'Speed' },
  { id: 'altitude', label: 'Elevation' },
];

export function defaultSession(): ComparisonSession {
  return {
    mode: 'two-workouts',
    viewMode: 'full',
    metric: 'power',
    alignment: 'time',
    workoutA: null,
    workoutB: null,
    segmentA: null,
    segmentB: null,
  };
}

export function sessionFromPrefill(prefill: ComparisonPrefill | null): ComparisonSession {
  const base = defaultSession();
  if (!prefill) return base;
  const mode = prefill.mode ?? base.mode;
  return {
    ...base,
    mode,
    viewMode:
      mode === 'one-workout-segments' || prefill.segmentA != null ? 'segments' : base.viewMode,
    segmentA: prefill.segmentA ?? null,
    segmentB: prefill.segmentB ?? null,
  };
}
